import React from 'react';
import styled, { keyframes } from 'styled-components';
import { useTranslation } from 'react-i18next';

export const DEFAULT_PHOTO_SLOTS = [
  { id: 'front', labelKey: 'listing.photoSlotFront', hintKey: 'listing.photoSlotFrontHint' },
  { id: 'back', labelKey: 'listing.photoSlotBack', hintKey: 'listing.photoSlotBackHint' },
  { id: 'side', labelKey: 'listing.photoSlotSide', hintKey: 'listing.photoSlotSideHint' },
  { id: 'closeup', labelKey: 'listing.photoSlotCloseup', hintKey: 'listing.photoSlotCloseupHint' },
];

const CameraGlyph = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M7.2 7.1h1.05l.62-1.28A1.4 1.4 0 0 1 10.12 5h3.76a1.4 1.4 0 0 1 1.25.82L15.75 7.1H16.8A2.7 2.7 0 0 1 19.5 9.8v6.5A2.7 2.7 0 0 1 16.8 19H7.2A2.7 2.7 0 0 1 4.5 16.3V9.8A2.7 2.7 0 0 1 7.2 7.1Z"
      stroke="currentColor"
      strokeWidth="1.55"
      strokeLinejoin="round"
    />
    <circle cx="12" cy="13" r="2.85" stroke="currentColor" strokeWidth="1.55" />
  </svg>
);

const PlusGlyph = ({ size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
    <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const Studio = styled.div`
  display: grid;
  grid-template-columns: minmax(200px, 1.25fr) minmax(150px, 1fr);
  gap: 0.7rem;
  margin-top: 0.2rem;

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const EmptyDropzone = styled.button`
  width: 100%;
  min-height: 228px;
  margin-top: 0.2rem;
  padding: 1.75rem 1.2rem 1.4rem;
  border: 1.5px dashed #c5bba8;
  border-radius: 1.15rem;
  background: linear-gradient(180deg, #fbf7ef 0%, #fff 72%);
  color: inherit;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.55rem;
  transition: border-color 0.18s, box-shadow 0.18s, background 0.18s, transform 0.18s;

  &:hover {
    border-color: #10b981;
    background: linear-gradient(180deg, #f0fdf4 0%, #fff 80%);
    box-shadow: 0 14px 32px rgba(16, 185, 129, 0.1);
    transform: translateY(-1px);
  }

  &:focus-visible {
    outline: 2px solid #10b981;
    outline-offset: 3px;
  }
`;

const IconWell = styled.span`
  width: 3.4rem;
  height: 3.4rem;
  border-radius: 1rem;
  background: #111827;
  color: #f8fafc;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 10px 22px rgba(17, 24, 39, 0.18);
`;

const EmptyTitle = styled.strong`
  font-size: 1.05rem;
  font-weight: 700;
  color: #111827;
  letter-spacing: -0.02em;
`;

const EmptyHint = styled.span`
  max-width: 26rem;
  font-size: 0.84rem;
  line-height: 1.45;
  color: #64748b;
`;

const AngleRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.4rem;
  margin-top: 0.35rem;
`;

const AngleChip = styled.span`
  padding: 0.28rem 0.62rem;
  border-radius: 999px;
  background: #fff;
  border: 1px solid #e7e0d4;
  color: #44403c;
  font-size: 0.72rem;
  font-weight: 650;
`;

const Frame = styled.div`
  position: relative;
  overflow: hidden;
  background: #111827;
  border: 1px solid ${(p) => (p.$cover ? '#d6d3d1' : '#e7e5e4')};
  border-radius: ${(p) => (p.$cover ? '1.15rem' : '0.9rem')};
  box-shadow: ${(p) => (p.$cover ? '0 16px 36px rgba(15, 23, 42, 0.1)' : 'none')};
`;

const CoverFrame = styled(Frame)`
  aspect-ratio: 4 / 5;
  min-height: 240px;

  @media (max-width: 560px) {
    aspect-ratio: 4 / 3;
    min-height: 188px;
  }
`;

const ThumbGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.55rem;
  align-content: start;
`;

const ThumbFrame = styled(Frame)`
  aspect-ratio: 1;
`;

const AddTile = styled.button`
  aspect-ratio: 1;
  border-radius: 0.9rem;
  border: 1.5px dashed #c5bba8;
  background: #fbf7ef;
  color: #292524;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.3rem;
  transition: border-color 0.15s, background 0.15s, color 0.15s;

  &:hover {
    border-color: #10b981;
    background: #ecfdf5;
    color: #047857;
  }

  &:focus-visible {
    outline: 2px solid #10b981;
    outline-offset: 2px;
  }
`;

const AddTileLabel = styled.span`
  font-size: 0.72rem;
  font-weight: 700;
`;

const PreviewImg = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const PreviewVideo = styled.video`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  background: #111827;
`;

const Badge = styled.span`
  position: absolute;
  top: 0.55rem;
  left: 0.55rem;
  z-index: 2;
  padding: 0.22rem 0.55rem;
  border-radius: 999px;
  background: rgba(17, 24, 39, 0.86);
  color: #fff;
  font-size: 0.68rem;
  font-weight: 750;
  letter-spacing: 0.02em;
`;

const MediaBadge = styled(Badge)`
  top: auto;
  bottom: 0.55rem;
  left: 0.55rem;
`;

const IconBtn = styled.button`
  position: absolute;
  z-index: 3;
  border: none;
  cursor: pointer;
  color: #fff;
  font-weight: 800;
`;

const RemoveBtn = styled(IconBtn)`
  top: 0.5rem;
  right: 0.5rem;
  width: 1.7rem;
  height: 1.7rem;
  border-radius: 999px;
  background: rgba(17, 24, 39, 0.78);
  font-size: 1.05rem;
  line-height: 1;

  &:hover {
    background: #b91c1c;
  }
`;

const EditBtn = styled(IconBtn)`
  left: 0.5rem;
  bottom: 0.5rem;
  padding: 0.32rem 0.62rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.94);
  color: #111827;
  font-size: 0.7rem;
  font-weight: 750;
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const UploadOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.2rem;
  background: rgba(17, 24, 39, 0.52);
  backdrop-filter: blur(2px);
`;

const Spinner = styled.div`
  width: 1.9rem;
  height: 1.9rem;
  border-radius: 999px;
  border: 3px solid rgba(255, 255, 255, 0.35);
  border-top-color: #10b981;
  animation: ${spin} 0.9s linear infinite;
`;

const RingWrap = styled.div`
  position: relative;
  width: 2.6rem;
  height: 2.6rem;
`;

const RingSvg = styled.svg`
  width: 100%;
  height: 100%;
  transform: rotate(-90deg);
`;

const RingPercent = styled.span`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 0.62rem;
  font-weight: 800;
`;

const UploadLabel = styled.span`
  color: #e2e8f0;
  font-size: 0.62rem;
  font-weight: 700;
`;

const ErrorOverlay = styled(UploadOverlay)`
  background: rgba(127, 29, 29, 0.62);
`;

const RetryBtn = styled.button`
  border: none;
  cursor: pointer;
  border-radius: 999px;
  padding: 0.28rem 0.6rem;
  background: #fff;
  color: #b91c1c;
  font-size: 0.62rem;
  font-weight: 800;
`;

const DoneBadge = styled.span`
  position: absolute;
  right: 0.5rem;
  bottom: 0.5rem;
  z-index: 2;
  width: 1.35rem;
  height: 1.35rem;
  border-radius: 999px;
  background: #059669;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.72rem;
  font-weight: 900;
`;

const UploadProgressRing = ({ progress }) => {
  const pct = Math.max(0, Math.min(100, Math.round(progress || 0)));
  if (pct <= 0) {
    return <Spinner aria-hidden />;
  }
  return (
    <RingWrap>
      <RingSvg viewBox="0 0 44 44" aria-hidden>
        <circle cx="22" cy="22" r="18" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="5" />
        <circle
          cx="22"
          cy="22"
          r="18"
          fill="none"
          stroke="#10b981"
          strokeWidth="5"
          strokeLinecap="round"
          pathLength="100"
          strokeDasharray={`${pct} 100`}
        />
      </RingSvg>
      <RingPercent>{pct}%</RingPercent>
    </RingWrap>
  );
};

function mediaSrc(item) {
  return item?.preview || item?.uri || '';
}

function isVideoItem(item) {
  return Boolean(item?.isVideo || (item?.type || '').startsWith('video/'));
}

const MediaOverlays = ({
  item,
  index,
  isCover,
  coverLabel,
  t,
  onRemove,
  onEdit,
  onRetry,
}) => {
  const upload = item?.upload;
  const uploading = upload?.status === 'uploading';
  const uploadError = upload?.status === 'error';
  const uploadDone = upload?.status === 'done';
  const isVideo = isVideoItem(item);

  return (
    <>
      <Badge>{isCover ? coverLabel : index + 1}</Badge>
      {isVideo ? <MediaBadge>{t('listing.videoBadge')}</MediaBadge> : null}
      {uploading ? (
        <UploadOverlay>
          <UploadProgressRing progress={upload?.progress} />
          <UploadLabel>{t('listing.uploadingShort')}</UploadLabel>
        </UploadOverlay>
      ) : null}
      {uploadError ? (
        <ErrorOverlay>
          <UploadLabel>{t('listing.uploadFailedShort')}</UploadLabel>
          {onRetry ? (
            <RetryBtn type="button" onClick={() => onRetry(index)}>
              {t('listing.retryUpload')}
            </RetryBtn>
          ) : null}
        </ErrorOverlay>
      ) : null}
      {uploadDone ? <DoneBadge aria-label={t('listing.uploadDone')}>✓</DoneBadge> : null}
      {onRemove ? (
        <RemoveBtn type="button" onClick={() => onRemove(index)} aria-label={t('listing.removePhoto')}>
          ×
        </RemoveBtn>
      ) : null}
      {!isVideo && !uploading && onEdit ? (
        <EditBtn type="button" onClick={() => onEdit(index)}>
          {t('listing.edit')}
        </EditBtn>
      ) : null}
    </>
  );
};

const FilledMedia = ({ item, FrameComponent, ...overlayProps }) => {
  const src = mediaSrc(item);
  const isVideo = isVideoItem(item);
  return (
    <FrameComponent $cover={overlayProps.isCover}>
      {isVideo ? (
        <PreviewVideo src={src} muted preload="metadata" />
      ) : (
        <PreviewImg src={src} alt={overlayProps.isCover ? overlayProps.coverLabel : overlayProps.t('listing.photoSlotN', { number: overlayProps.index + 1 })} />
      )}
      <MediaOverlays {...overlayProps} item={item} />
    </FrameComponent>
  );
};

/**
 * Cover-first listing media picker. Empty state is one studio dropzone;
 * filled state keeps the first file large and stacks extras beside it.
 */
const PhotoSlotGrid = ({
  items = [],
  maxSlots = 5,
  onPickSlot,
  onRemove,
  onEdit,
  onRetry,
  coverLabel,
  supportsVideo = false,
}) => {
  const { t } = useTranslation();
  const resolvedCoverLabel = coverLabel || t('listing.cover');
  const canAdd = items.length < maxSlots;

  if (items.length === 0) {
    return (
      <EmptyDropzone
        type="button"
        onClick={() => onPickSlot?.(0)}
        aria-label={supportsVideo ? t('listing.addPhotosCtaVideo') : t('listing.addPhotosCta')}
      >
        <IconWell>
          <CameraGlyph />
        </IconWell>
        <EmptyTitle>
          {supportsVideo ? t('listing.addPhotosCtaVideo') : t('listing.addPhotosCta')}
        </EmptyTitle>
        <EmptyHint>
          {supportsVideo ? t('listing.addPhotosEmptyHintVideo') : t('listing.addPhotosEmptyHint')}
        </EmptyHint>
        <AngleRow>
          {DEFAULT_PHOTO_SLOTS.map((slot) => (
            <AngleChip key={slot.id}>{t(slot.labelKey)}</AngleChip>
          ))}
        </AngleRow>
      </EmptyDropzone>
    );
  }

  const extras = items.slice(1);

  return (
    <Studio>
      <FilledMedia
        item={items[0]}
        FrameComponent={CoverFrame}
        index={0}
        isCover
        coverLabel={resolvedCoverLabel}
        t={t}
        onRemove={onRemove}
        onEdit={onEdit}
        onRetry={onRetry}
      />
      <ThumbGrid>
        {extras.map((item, extraIndex) => (
          <FilledMedia
            key={`${mediaSrc(item) || extraIndex}-${extraIndex + 1}`}
            item={item}
            FrameComponent={ThumbFrame}
            index={extraIndex + 1}
            isCover={false}
            coverLabel={resolvedCoverLabel}
            t={t}
            onRemove={onRemove}
            onEdit={onEdit}
            onRetry={onRetry}
          />
        ))}
        {canAdd ? (
          <AddTile
            type="button"
            onClick={() => onPickSlot?.(items.length)}
            aria-label={t('listing.addMoreMedia')}
          >
            <PlusGlyph />
            <AddTileLabel>{t('listing.addMoreMedia')}</AddTileLabel>
          </AddTile>
        ) : null}
      </ThumbGrid>
    </Studio>
  );
};

export default PhotoSlotGrid;
